/**
 * Wizard card for the stripe_platform_webhook step (Slice 1).
 *
 * This is a platform-level, run-once step: ONE Stripe Connect webhook
 * endpoint serves every client. The edge function searches existing
 * endpoints by URL and only creates when absent. The signing secret is
 * returned by Stripe ONLY at creation time, so the card surfaces it once
 * with instructions to store it as the STRIPE_CONNECT_WEBHOOK_SECRET env
 * secret — after that, re-runs just confirm the endpoint exists.
 */
import { CheckCircle2, Loader2, RefreshCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

interface StripePlatformWebhookCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface PlatformWebhookResult {
  endpointId?: string
  webhookUrl?: string
  secret?: string
  alreadyExisted?: boolean
}

export function StripePlatformWebhookCard({
  task,
  accountId,
  runId,
}: StripePlatformWebhookCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as PlatformWebhookResult

  const runStep = () => {
    invokeStep.mutate(
      { functionName: 'onboard-stripe-platform-webhook', accountId },
      {
        onError: (error) =>
          toast({
            title: 'Platform webhook step failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  return (
    <StepCard
      task={task}
      manualFallback={
        <p>
          Manual path: in the platform Stripe dashboard, create a webhook
          endpoint with “Listen on Connected accounts” enabled, pointing at
          the viewer&apos;s /api/webhook/stripe URL, with events
          payment_intent.succeeded, payment_intent.payment_failed,
          charge.refunded, and account.application.deauthorized. Store the
          signing secret as STRIPE_CONNECT_WEBHOOK_SECRET.
        </p>
      }
    >
      {result.endpointId && (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              Endpoint{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {result.endpointId}
              </code>
              {result.alreadyExisted ? ' (already existed)' : ' created'}
              {result.webhookUrl && (
                <span className="text-muted-foreground"> → {result.webhookUrl}</span>
              )}
            </span>
          </div>
          {result.secret && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
              <p className="mb-1 flex items-center gap-1 font-medium">
                <TriangleAlert className="h-4 w-4" />
                One-time signing secret — store it now
              </p>
              <code className="block break-all rounded bg-amber-100 px-1.5 py-0.5 font-mono text-xs">
                {result.secret}
              </code>
              <p className="mt-1 text-xs">
                Set this as STRIPE_CONNECT_WEBHOOK_SECRET in the viewer app
                env. Stripe will not show it again.
              </p>
            </div>
          )}
        </div>
      )}
      <Button
        type="button"
        size="sm"
        variant={result.endpointId ? 'outline' : 'default'}
        onClick={runStep}
        disabled={invokeStep.isPending}
      >
        {invokeStep.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="mr-2 h-4 w-4" />
        )}
        {result.endpointId ? 'Re-check endpoint' : 'Ensure platform webhook'}
      </Button>
    </StepCard>
  )
}
